/**
 * App-wide keyboard shortcuts. One meaning per key:
 * - ⌘K / Ctrl+K opens global search (the Search button in the sidebar's Rail Header shows it).
 * - "/" focuses the search field on the current page (in-page fields show "/").
 * Neither fires while the user is typing in a field, except ⌘K, which always opens search.
 */
import { useEffect, type RefObject } from "react";

const typing = (t: EventTarget | null) =>
  typeof HTMLElement !== "undefined" && t instanceof HTMLElement && (t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT");

export const isGlobalSearchKey = (e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey">) => (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k";
export const isPageSearchKey = (e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "target">) =>
  e.key === "/" && !e.metaKey && !e.ctrlKey && !e.altKey && !typing(e.target);

export function useGlobalSearchShortcut(open: (() => void) | undefined) {
  useEffect(() => {
    if (!open) return;
    const on = (e: KeyboardEvent) => { if (isGlobalSearchKey(e)) { e.preventDefault(); open(); } };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [open]);
}

/** Give the page's search input a ref; "/" focuses it. */
export function useSlashToFocus(ref: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const on = (e: KeyboardEvent) => { if (isPageSearchKey(e) && ref.current) { e.preventDefault(); ref.current.focus(); ref.current.select(); } };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, [ref]);
}
